import React, { useState } from 'react';
import { useRouterParam } from '../../../../model/history';
import { Container } from '../../../../components/Container/Container';
import { getPageTitle, useMeta } from '../../../../model/meta';
import { ordersPath, orderPath } from '../../../../routes';
import { useRemoteData } from '../../../../model/data';
import { getOrder } from '../../../../model/order';
import { Card } from '../../../../components/Card/Card';
import { LoadingPage } from '../../../../components/LoadingPage/LoadingPage';
import { PermissionButton } from '../../../../components/Button/PermissionButton';
import { StatusPill } from '../../../../components/StatusPill/StatusPill';
import { ControlledModal } from '../../../../components/Modal/ControlledModal';
import { Address } from '../../../../model/company';
import { EditDelivery } from './EditDelivery';
import { Button } from '../../../../components/Button/Button';
import {
  updateDelivery,
  getDeliveryStatus,
  DeliveryStatus,
  removeDeliveryFromPicklist,
} from '../../../../model/delivery';
import { handleError } from '../../../../model/notification';
import { t } from '../../../../locales/translate';
import { useReloadKey } from '../../../../util/util';
import { SimpleCheckbox } from '../../../../components/Form/Checkbox/Checkbox';
import { Icon } from '../../../../components/Icon/Icon';
import { Tooltip } from '../../../../components/Tooltip/Tooltip';
import { useConfirmation } from '../../../../components/ConfirmationPrompt/ConfirmationPrompt';

export function Delivery(): JSX.Element {
  const [editModal, setEditModal] = useState<'editAll' | 'editTrackingUrl' | undefined>(undefined);
  const { orderId, deliveryId } = useRouterParam<{
    orderId: string;
    deliveryId: string;
  }>();
  const [key, reload] = useReloadKey();
  const [delivery, loading, error] = useRemoteData(
    () => getOrder(orderId),
    (db) => {
      const order = db.table('orders').find(orderId);
      if (order) {
        const orderDelivery = order.deliveries.find((item) => item.id === deliveryId);
        if (orderDelivery) {
          return {
            order,
            ...orderDelivery,
          };
        }
      }
      return undefined;
    }
  );

  const [loadingUpdateStatus, setLoadingUpdateStatus] = useState(false);
  const [loadingIframe, setLoadingIframe] = useState(true);

  useMeta({
    title: getPageTitle(loading && !delivery, delivery ? delivery.order.reference : 'not found', delivery),
    breadcrumbs: [
      { title: t.model.order._plural, url: ordersPath() },
      {
        title: getPageTitle(loading && !delivery, delivery ? delivery.order.reference : 'not found', delivery),
        url: orderPath(delivery ? delivery.order.id : ''),
      },
      {
        title: getPageTitle(loading && !delivery, delivery ? delivery.reference : 'not found', delivery),
      },
    ],
  });

  const [skipEmailDelivery, setSkipEmailDelivery] = useState(false);

  async function updateDeliveryStatus(status: DeliveryStatus): Promise<void> {
    if (!delivery) {
      return;
    }

    setLoadingUpdateStatus(true);
    try {
      await updateDelivery(delivery.id, {
        orderId: delivery.order.id,
        status,
        skipEmailDelivery,
      });
    } catch (error) {
      handleError(error);
    }
    setLoadingUpdateStatus(false);
  }

  function activateButton(): JSX.Element | null {
    if (!delivery) {
      return null;
    }

    switch (delivery.status) {
      case 'draft':
        return (
          <Button
            type="secondary"
            onClick={() => updateDeliveryStatus('pending')}
            title={t.actions.place(t.model.delivery._name)}
            loading={loadingUpdateStatus}
          />
        );
      case 'pending':
      case 'fulfillment':
        return (
          <Button
            type="secondary"
            onClick={() => updateDeliveryStatus('sent')}
            title={t.actions.book(t.model.delivery._name)}
            loading={loadingUpdateStatus}
          />
        );
      default:
        return null;
    }
  }

  async function removeFromPicklist(): Promise<void> {
    if (!delivery?.picklistId) {
      return;
    }

    await removeDeliveryFromPicklist(delivery.id);
    if (delivery.orderId) {
      await getOrder(delivery.orderId);
    }
  }

  const { confirm } = useConfirmation({
    title: t.model.delivery.removeFromPicklist,
    message: t.model.delivery.confirmRemoveFromPicklist,
    action: removeFromPicklist,
  });

  function removeFromPicklistButton(): JSX.Element | null {
    if (!delivery || !delivery.picklistId) {
      return null;
    }

    const button = <PermissionButton model={delivery} permission="update" name="remove-circle" onClick={confirm} />;
    return (
      <Tooltip reference={button} darkMode>
        {t.model.delivery.removeFromPicklist}
      </Tooltip>
    );
  }

  if (loading) {
    return <LoadingPage title={t.actions.loading(t.model.delivery._name)} />;
  } else if (error || !delivery) {
    return <Container>{t.errors.itemNotFound(t.model.delivery._name)}.</Container>;
  }

  return (
    <Container className="delivery-details">
      <Card
        title={
          <>
            {`${t.model.delivery._name} - ${delivery.reference} `}
            <StatusPill status={getDeliveryStatus(delivery.status)} />
          </>
        }
        actions={
          <>
            {delivery.status !== 'sent' && (
              <SimpleCheckbox
                value={skipEmailDelivery}
                onChange={() => setSkipEmailDelivery((value) => !value)}
                label={t.model.payment.skipCustomerEmail}
              ></SimpleCheckbox>
            )}

            {activateButton()}
            {removeFromPicklistButton()}

            <PermissionButton
              model={delivery}
              permission="update"
              name="edit"
              onClick={() => setEditModal('editAll')}
            />

            <Tooltip reference={<Icon name="tracking" onClick={() => setEditModal('editTrackingUrl')}></Icon>} darkMode>
              {t.actions.edit(t.model.delivery.trackingCode)}
            </Tooltip>
          </>
        }
      >
        {loadingIframe && (
          <span style={{ display: 'flex', alignContent: 'center' }}>
            <LoadingPage title={t.actions.loading(t.model.delivery._name)} />
          </span>
        )}
        <iframe
          key={key}
          onLoad={() => {
            setLoadingIframe(false);
          }}
          src={delivery.documentUrl}
          width="100%"
          frameBorder="0"
          style={{ height: 'calc(100vh - 200px)' }}
        />
      </Card>
      <ControlledModal<Address>
        open={!!editModal}
        model={delivery.address}
        onClose={() => setEditModal(undefined)}
        title={t.actions.edit(t.model.delivery._name)}
        size="small"
      >
        {(controller) => (
          <EditDelivery
            onSubmit={() => {
              setLoadingIframe(true);
              reload();
            }}
            reduced={editModal === 'editTrackingUrl'}
            delivery={delivery}
            controller={controller}
          />
        )}
      </ControlledModal>
    </Container>
  );
}
